/**
 * 成本统计 / 我的用量 (移植自旧版 www/legacy/cost.html，并合入 admin.html 的按用户 / 按公司统计)。
 *
 * 所有登录用户可进入；数据范围由后端按角色隔离：
 *   user          → 只有自己的数据，页面标题「我的用量」
 *   company_admin → 本公司；多一个「按用户」
 *   super_admin   → 全部；多「按用户」「按公司」
 */
import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { Button, Card, CardContent, CardHeader, PageHeader, Select, Tabs } from "@/components/ui";
import { MonthSelector } from "../components/MonthSelector";
import { StatsTable, type Column } from "../components/StatsTable";
import {
  useDailyStats,
  useMonthlyStats,
  useByModelStats,
  useByUserStats,
  useByCompanyStats,
} from "../hooks";
import type {
  UsageTotals, DailyStatRow, MonthlyStatRow, ByModelRow, ByUserRow, ByCompanyRow,
} from "../api";
import { PRICES, DEFAULT_MODEL, calcCost, calcCostByModel, fmtCost, fmtNum } from "../pricing";
import { LazyColumnChart } from "@/features/dashboard/components/LazyCharts";
import { ShareBars } from "@/features/dashboard/components/ShareBars";
import {
  buildDailySeries, compactNum, daysOfMonth, shareItems, ymdInTz,
} from "@/features/dashboard/series";

type Tab = "daily" | "monthly" | "by-model" | "by-user" | "by-company";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const ZERO: UsageTotals = {
  requests: 0, user_queries: 0, input_tokens: 0, output_tokens: 0, total_tokens: 0, image_count: 0,
};

function sumRows(rows: UsageTotals[] | undefined): UsageTotals {
  const t = { ...ZERO };
  for (const r of rows ?? []) {
    t.requests += r.requests || 0;
    t.user_queries += r.user_queries || 0;
    t.input_tokens += r.input_tokens || 0;
    t.output_tokens += r.output_tokens || 0;
    t.total_tokens += r.total_tokens || 0;
    t.image_count += r.image_count || 0;
  }
  return t;
}

/** 汇总类表格共用的数值列 */
function usageColumns<T extends UsageTotals>(model: string, showCost: boolean): Column<T>[] {
  const cols: Column<T>[] = [
    { key: "user_queries", header: "用户提问", numeric: true },
    { key: "requests", header: "API 请求次数", numeric: true },
    { key: "input_tokens", header: "Input Tokens", numeric: true },
    { key: "output_tokens", header: "Output Tokens", numeric: true },
    { key: "total_tokens", header: "Total Tokens", numeric: true },
    { key: "image_count", header: "图片", numeric: true },
    {
      key: "cost", header: "预估费用 (USD)", numeric: true, className: "font-medium text-price",
      render: (r: T) => fmtCost(calcCost(r, model)),
    },
  ];
  return showCost ? cols : cols.filter((c) => c.key !== "cost");
}

function usageTotals(t: UsageTotals, cost: number, showCost: boolean) {
  const row: Record<string, string | number> = {
    user_queries: t.user_queries,
    requests: t.requests,
    input_tokens: t.input_tokens,
    output_tokens: t.output_tokens,
    total_tokens: t.total_tokens,
    image_count: t.image_count,
  };
  if (showCost) row.cost = fmtCost(cost);
  return row;
}

function ChartCard({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} description={sub} />
      <CardContent className="pt-3 sm:pt-3">{children}</CardContent>
    </Card>
  );
}

export function CostPage() {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const isUser = role === "user";
  // 与后端权限一致：by-user 需 company_admin / super_admin，by-company 仅 super_admin
  const canByUser = role === "company_admin" || role === "super_admin";
  const canByCompany = role === "super_admin";
  // 上游模型单价 / USD 成本估算只给平台超管看; 公司管理员和普通用户按积分付费, 只看用量
  const showCost = role === "super_admin";

  const [tab, setTab] = useState<Tab>("daily");
  const [month, setMonth] = useState<string>(currentMonth());
  const [model, setModel] = useState<string>(DEFAULT_MODEL);

  const daily = useDailyStats(month);
  const monthly = useMonthlyStats(12);
  const byModel = useByModelStats(month);
  const byUser = useByUserStats(month, canByUser && tab === "by-user");
  const byCompany = useByCompanyStats(month, canByCompany && tab === "by-company");

  const dailyTotals = useMemo(() => sumRows(daily.data?.data), [daily.data]);
  const dailyCost = useMemo(
    () => (daily.data?.data ?? []).reduce((s, r) => s + calcCost(r, model), 0),
    [daily.data, model],
  );
  const monthlyTotals = useMemo(() => sumRows(monthly.data?.data), [monthly.data]);
  const monthlyCost = useMemo(
    () => (monthly.data?.data ?? []).reduce((s, r) => s + calcCost(r, model), 0),
    [monthly.data, model],
  );
  const byModelTotals = useMemo(() => sumRows(byModel.data?.data), [byModel.data]);
  const byModelCost = useMemo(
    () => (byModel.data?.data ?? []).reduce((s, r) => s + calcCostByModel(r, r.model, model).cost, 0),
    [byModel.data, model],
  );
  const byUserTotals = useMemo(() => sumRows(byUser.data?.data), [byUser.data]);
  const byCompanyTotals = useMemo(() => sumRows(byCompany.data?.data), [byCompany.data]);

  // 表格上方的图表 (与仪表盘同一套样式)。每日: 整月逐日补 0, 当月只画到今天
  const today = useMemo(() => ymdInTz(new Date()), []);
  const dailyChart = useMemo(
    () => buildDailySeries(daysOfMonth(month, month === today.slice(0, 7) ? today : undefined), daily.data?.data)
      .map((p) => ({ label: p.label, title: p.date, value: p.user_queries })),
    [month, today, daily.data],
  );
  const monthlyChart = useMemo(
    () => [...(monthly.data?.data ?? [])]
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => ({ label: r.month, value: r.user_queries || 0 })),
    [monthly.data],
  );
  const modelShare = useMemo(
    () => shareItems(byModel.data?.data, (r) => r.model || "未知", (r) => r.total_tokens, 8),
    [byModel.data],
  );
  const userShare = useMemo(
    () => shareItems(byUser.data?.data, (r) => r.display_name || r.username, (r) => r.user_queries, 8),
    [byUser.data],
  );
  const companyShare = useMemo(
    () => shareItems(byCompany.data?.data, (r) => r.company_name, (r) => r.user_queries, 8),
    [byCompany.data],
  );

  const refreshing = daily.isFetching || monthly.isFetching || byModel.isFetching
    || byUser.isFetching || byCompany.isFetching;
  const refresh = () => qc.invalidateQueries({ queryKey: ["stats"] });

  const title = isUser ? "我的用量" : showCost ? "成本统计" : "用量统计";
  const hasDaily = (daily.data?.data.length ?? 0) > 0;
  const summary: Array<[string, string, string]> = [
    ["用户提问", hasDaily ? fmtNum(dailyTotals.user_queries) : "—", "次"],
    ["Input Tokens", hasDaily ? fmtNum(dailyTotals.input_tokens) : "—", "tokens"],
    ["Output Tokens", hasDaily ? fmtNum(dailyTotals.output_tokens) : "—", "tokens"],
    ["Total Tokens", hasDaily ? fmtNum(dailyTotals.total_tokens) : "—", "tokens"],
    ["图片生成", hasDaily ? fmtNum(dailyTotals.image_count) : "—", "张"],
    ...(showCost ? [["预估费用", hasDaily ? fmtCost(dailyCost) : "—", "USD"] as [string, string, string]] : []),
  ];

  const tabs: Array<[Tab, string]> = [
    ["daily", "每日明细"],
    ["monthly", "月度汇总"],
    ["by-model", "按模型"],
    ...(canByUser ? [["by-user", "按用户"] as [Tab, string]] : []),
    ...(canByCompany ? [["by-company", "按公司"] as [Tab, string]] : []),
  ];

  const sharedCols = usageColumns<UsageTotals>(model, showCost);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:space-y-5 sm:p-6">
      <PageHeader
        title={title}
        description={showCost ? `Token 用量与成本估算（${month}）` : `提问、Token 与图片用量（${month}）`}
        actions={
          <>
            {showCost && (
              <label className="flex w-full items-center gap-2 text-sm text-muted-foreground sm:w-auto">
                <span className="shrink-0">模型</span>
                <Select value={model} onChange={(e) => setModel(e.target.value)} className="min-w-0 flex-1 sm:w-44 sm:flex-none">
                  {PRICES.map((p) => <option key={p.model} value={p.model}>{p.model}</option>)}
                </Select>
              </label>
            )}
            <MonthSelector value={month} onChange={setMonth} />
            <Button
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              icon={<RefreshCw className={cn(refreshing && "animate-spin")} />}
            >
              刷新
            </Button>
          </>
        }
      />

      {/* 当月汇总（来自每日明细合计） */}
      <div className={cn("grid grid-cols-3 gap-2 sm:gap-3", showCost ? "lg:grid-cols-6" : "lg:grid-cols-5")}>
        {summary.map(([label, value, unit], i) => (
          <div key={label} className="min-w-0 rounded-xl border bg-card px-2.5 py-2 shadow-sm sm:p-4">
            <div className="truncate text-xs text-muted-foreground">{label}</div>
            <div
              className={cn(
                "mt-0.5 truncate text-base font-semibold tabular-nums sm:mt-1 sm:text-xl",
                showCost && i === summary.length - 1 ? "text-price" : "text-foreground",
              )}
              title={value}
            >
              {value}
            </div>
            <div className="text-xs text-muted-foreground">{unit}</div>
          </div>
        ))}
      </div>

      <Tabs
        aria-label="统计维度"
        items={tabs.map(([value, label]) => ({ value, label }))}
        value={tab}
        onValueChange={setTab}
      />

      {tab === "daily" && hasDaily && (
        <ChartCard title="每日提问次数" sub={month}>
          <LazyColumnChart data={dailyChart} name="提问次数" />
        </ChartCard>
      )}
      {tab === "monthly" && monthlyChart.length > 0 && (
        <ChartCard title="月度提问次数" sub="近 12 个月">
          <LazyColumnChart data={monthlyChart} name="提问次数" />
        </ChartCard>
      )}
      {tab === "by-model" && modelShare.length > 0 && (
        <ChartCard title="模型用量分布" sub={`${month} · 按 Total Tokens`}>
          <ShareBars items={modelShare} format={compactNum} />
        </ChartCard>
      )}
      {tab === "by-user" && canByUser && userShare.length > 0 && (
        <ChartCard title="用户提问分布" sub={month}>
          <ShareBars items={userShare} />
        </ChartCard>
      )}
      {tab === "by-company" && canByCompany && companyShare.length > 0 && (
        <ChartCard title="公司提问分布" sub={month}>
          <ShareBars items={companyShare} />
        </ChartCard>
      )}

      {tab === "daily" && (
        <StatsTable<DailyStatRow>
          rows={daily.data?.data}
          loading={daily.isLoading}
          columns={[{ key: "date", header: "日期" }, ...sharedCols]}
          totals={usageTotals(dailyTotals, dailyCost, showCost)}
        />
      )}

      {tab === "monthly" && (
        <StatsTable<MonthlyStatRow>
          rows={monthly.data?.data}
          loading={monthly.isLoading}
          columns={[{ key: "month", header: "月份" }, ...sharedCols]}
          totals={usageTotals(monthlyTotals, monthlyCost, showCost)}
        />
      )}

      {tab === "by-model" && (
        <StatsTable<ByModelRow>
          rows={byModel.data?.data}
          loading={byModel.isLoading}
          columns={[
            {
              key: "model", header: "模型",
              render: (r) => r.model || <span className="text-muted-foreground">未知</span>,
            },
            { key: "user_queries", header: "用户提问", numeric: true },
            { key: "requests", header: "API 请求次数", numeric: true },
            { key: "input_tokens", header: "Input Tokens", numeric: true },
            { key: "output_tokens", header: "Output Tokens", numeric: true },
            { key: "image_count", header: "图片", numeric: true },
            ...(showCost ? [{
              key: "price_in", header: "单价 In ($/1M)", numeric: true, className: "text-muted-foreground",
              render: (r) => `$${calcCostByModel(r, r.model, model).price.input}`,
            },
            {
              key: "price_out", header: "单价 Out ($/1M)", numeric: true, className: "text-muted-foreground",
              render: (r) => `$${calcCostByModel(r, r.model, model).price.output}`,
            },
            {
              key: "cost", header: "精准费用 (USD)", numeric: true, className: "font-medium text-price",
              render: (r) => fmtCost(calcCostByModel(r, r.model, model).cost),
            }] as Column<ByModelRow>[] : []),
          ]}
          totals={{
            user_queries: byModelTotals.user_queries,
            requests: byModelTotals.requests,
            input_tokens: byModelTotals.input_tokens,
            output_tokens: byModelTotals.output_tokens,
            image_count: byModelTotals.image_count,
            ...(showCost ? { cost: fmtCost(byModelCost) } : {}),
          }}
        />
      )}

      {tab === "by-user" && canByUser && (
        <StatsTable<ByUserRow>
          rows={byUser.data?.data}
          loading={byUser.isLoading}
          empty={byUser.isError ? "加载失败" : "暂无数据"}
          columns={[
            {
              key: "user", header: "用户",
              render: (r) => (
                <span>
                  {r.display_name || r.username}
                  {r.display_name && <span className="ml-1 text-xs text-muted-foreground font-mono">{r.username}</span>}
                </span>
              ),
            },
            { key: "role", header: "角色", className: "text-xs text-muted-foreground" },
            ...sharedCols,
          ]}
          totals={usageTotals(byUserTotals,
            (byUser.data?.data ?? []).reduce((s, r) => s + calcCost(r, model), 0), showCost)}
        />
      )}

      {tab === "by-company" && canByCompany && (
        <StatsTable<ByCompanyRow>
          rows={byCompany.data?.data}
          loading={byCompany.isLoading}
          empty={byCompany.isError ? "加载失败" : "暂无数据"}
          columns={[{ key: "company_name", header: "公司" }, ...sharedCols]}
          totals={usageTotals(byCompanyTotals,
            (byCompany.data?.data ?? []).reduce((s, r) => s + calcCost(r, model), 0), showCost)}
        />
      )}

      {showCost && (<>
      <p className="text-xs text-muted-foreground">
        预估费用 = Input Tokens × 输入单价 + Output Tokens × 输出单价（不含缓存价与图片）。
        「按模型」按每个模型自己的单价精准计算（未收录的模型用所选模型兜底），其余表格统一按所选模型 <span className="font-mono">{model}</span> 估算。
      </p>

      <section className="space-y-2">
        <h2 className="text-section">模型定价参考（$/1M tokens）</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PRICES.map((p) => {
            const active = p.model === model;
            return (
              <button
                key={p.model}
                type="button"
                onClick={() => setModel(p.model)}
                aria-pressed={active}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-left text-xs transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active ? "border-brand bg-primary-soft ring-1 ring-brand" : "hover:bg-muted",
                )}
              >
                <span className={cn("truncate font-mono", active && "font-medium text-primary")} title={p.model}>{p.model}</span>
                <span className="flex shrink-0 gap-2 tabular-nums text-muted-foreground">
                  <span>In: ${p.input}/1M</span>
                  {p.cached != null && <span>Cache: ${p.cached}/1M</span>}
                  <span className="text-foreground">Out: ${p.output}/1M</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>
      </>)}
    </div>
  );
}
